/**
 * useAstorContext Hook
 *
 * Provides access to the Astor context from within the AstorContextProvider tree.
 * Throws if used outside of the provider — intentional to catch integration errors early.
 *
 * @see docs/plans/2026-02-20-DESIGN-astor-ai-native-onboarding-help.md
 */

import { createContext, useContext } from 'react';

import type { AstorContextValue } from './astor-context.types';

/**
 * React context for the Astor system.
 * Initialized to null — the provider must be mounted for the hook to work.
 */
export const AstorContext = createContext<AstorContextValue | null>(null);

AstorContext.displayName = 'AstorContext';

/**
 * Access the Astor context (state + actions).
 *
 * @throws Error if called outside of <AstorContextProvider>
 */
export function useAstorContext(): AstorContextValue {
  const ctx = useContext(AstorContext);
  if (!ctx) {
    throw new Error('useAstorContext must be used within <AstorContextProvider>');
  }
  return ctx;
}
